import React, { useEffect, useState } from "react";
import { useHistory } from "react-router-dom";
import {
  Button,
  KeyValuePairs,
  Container,
  Header,
} from "@amzn/awsui-components-react-v3";
import {
  FormSchemaDto,
  FieldGroupDto,
} from "@amzn/crowd-front-end-common-library";
import WizardFooter from "../../wizard/WizardFooter/WizardFooter";
import { useStepResult } from "../../wizard/Wizard/WizardResultContextProvider";
import { CreateTeamWorkflowResult } from "../CreateTeamWorkflowResult";
import { CreateTeamContext } from "../CreateTeamContext";
import { useWizardWorkflowContext } from "../../wizard/Wizard/WizardWorkflowContextProvider";
import { useStepContext } from "../../wizard/Wizard/WizardStepContextProvider";
import { useHttpRequest, LoadingState } from "../../http";
import StaticFieldRenderer from "../../forms/StaticFieldRenderer/StaticFieldRenderer";
import { CreateTeamInputStepResult as InputStepResult } from "../inputStep/CreateTeamInputStepResult";
import styles from "../createTeam.module.css";
import InheritedAccess from "../inheritedAccess/inheritedAccessComponent";
import { MetricsState } from "../../logging";
import {
  createTeamSuccess,
  createTeamAccepted,
} from "./CreateTeamBannerMessage";
import { CustomStepInjectedProps } from "../../wizard/WizardStep/WizardStep";
import {
  teamFirstFailureBanner,
  teamAfterFirstFailureBanner,
} from "../../common/utils/ErrorBannerMessages";
import { BannerComponentId } from "../../banner/bannerComponentId";

const PREVIOUS_STEPS = ["Enter team details"];

const getPreviousStepsEditSection = (
  setStepNumber: React.Dispatch<React.SetStateAction<number>>
): JSX.Element[] => {
  const editSection: JSX.Element[] = PREVIOUS_STEPS.map((step, i) => {
    const onEdit = (): void => {
      setStepNumber(i);
    };

    return (
      <div className={styles["edit-button-section"]}>
        <div>Step {`${i + 1}: ${step}`}</div>
        <Button onClick={onEdit}>Edit</Button>
      </div>
    );
  });
  return editSection;
};

const getTeamInformation = (
  schema: FormSchemaDto,
  input: Partial<InputStepResult>,
  metrics: MetricsState,
  parentTeamId: string
): JSX.Element => {
  const groupLabel = "Team details";
  const teamGroup = schema.groups.find(
    (group) => group.groupLabel === groupLabel
  ) as FieldGroupDto;
  const { fieldDefinitions } = teamGroup;
  const fields = fieldDefinitions.map((fieldDefinition) => ({
    label: fieldDefinition.label,
    value: (
      <>
        <StaticFieldRenderer field={fieldDefinition} formValues={input} />
        {fieldDefinition.propertyName === "admins" && (
          <InheritedAccess metrics={metrics} parentTeamId={parentTeamId} />
        )}
      </>
    ),
  }));
  return (
    <Container header={<Header>{groupLabel}</Header>}>
      <KeyValuePairs items={fields} />
    </Container>
  );
};

const CreateTeamReviewStep: React.FC<CustomStepInjectedProps<undefined>> = ({
  goPrevious,
}): JSX.Element => {
  const [input] = useStepResult<CreateTeamWorkflowResult, "input">("input");
  const { metrics, parentTeamId, schema, setBanner } = useWizardWorkflowContext<
    CreateTeamContext
  >();

  const { setStepNumber } = useStepContext();

  const { loadingState, error, sendRequest, response } = useHttpRequest(
    "createTeam",
    metrics
  );
  const [submitTimes, setSubmitTimes] = useState(0);

  const history = useHistory();
  useEffect(() => {
    if (loadingState === LoadingState.RESOLVED) {
      if (response && response.status === 200) {
        setBanner(createTeamSuccess(input.name));
      } else {
        setBanner(createTeamAccepted(input.name));
      }
      history.push(`/team/${parentTeamId}`);
    } else if (loadingState === LoadingState.REJECTED) {
      if (submitTimes < 2) {
        setBanner(teamFirstFailureBanner("create", "created"));
      } else {
        setBanner(
          teamAfterFirstFailureBanner(
            "create",
            BannerComponentId.CreateTeamAfterFirstFailure
          )
        );
      }
    } else if (loadingState === LoadingState.LOADING) {
      setBanner(undefined);
    }
  }, [
    loadingState,
    history,
    parentTeamId,
    setBanner,
    input.name,
    response,
    submitTimes,
  ]);

  const owner = input.owner
    ? {
        employeeId: input.owner.attributes
          ? input.owner.attributes.employeeId
          : "",
      }
    : null;

  /**
   * Constructing admins array based on input provided in the CreateTeam form.
   * if (admins are provided in the form):
   *    if (owner is provided in the form):
   *        Add all the admins in the array as well as the owner
   *    else
   *        Add all the admins
   * else if (owner is provided in the form):
   *    Add only the owner
   * else
   *    return an empty array, meaning no admins
   */
  const admins = input.admins
    ? owner
      ? input.admins
          .map((admin) => ({
            employeeId: admin.attributes ? admin.attributes.employeeId : "",
          }))
          .filter((admin) => admin.employeeId !== owner.employeeId)
          .concat(owner)
      : input.admins.map((admin) => ({
          employeeId: admin.attributes ? admin.attributes.employeeId : "",
        }))
    : owner
    ? [owner]
    : [];

  const teamInformation = getTeamInformation(
    schema,
    input,
    metrics,
    parentTeamId
  );

  const previousStepsEditSection = getPreviousStepsEditSection(setStepNumber);

  const requestBody = {
    ...input,
    parentTeamId,
    owner,
    admins,
  };

  const onSubmit = (): void => {
    sendRequest({
      url: "/api/v1/teams/",
      method: "POST",
      data: requestBody,
    });
    setSubmitTimes((times) => times + 1);
  };

  const onPrevious = (): void => {
    setBanner(undefined);
    goPrevious();
  };

  return (
    <React.Fragment>
      <div className={styles["update-step-wrapper"]}>
        {previousStepsEditSection}
        {teamInformation}
        <WizardFooter
          onPrevious={onPrevious}
          onNext={onSubmit}
          nextButtonText="Create team"
          isLoading={loadingState === LoadingState.LOADING}
        />
      </div>
    </React.Fragment>
  );
};

export default CreateTeamReviewStep;
